import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useUser } from "../../context/UserContext";
import { Box } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import UserApi from "../../api/Admin/User";
import { GlobalButton } from "../../pages/styles";
import ClearIcon from "@mui/icons-material/Clear";
import { ValidationStatus, ValidationTextField } from "../ValidationTextField";
import { UserLookupModal } from "../UserLookupModal";
import { User } from "../../api/types";
import { exxonMobilDomains } from "../../global";

interface EmailAddressTextFieldProps {
  /**
   * @deprecated Use the style prop to pass fontSize instead. This prop will be removed in future iterations.
   * @example style={{ fontSize: '14px' }}
   */
  fontSize?: number;
  fieldWidth: string | undefined;
  validatedUserEmail: string | null;
  setValidatedUserEmail: (email: string) => void;
  /**
   * @deprecated Do not use this prop. Error state is managed internally and reported via setHasErrors callback. This prop will be removed in future iterations
   */
  hasErrors?: boolean | undefined;
  setHasErrors: (hasErrors: boolean | null) => void;
  isDisabled?: boolean;
  labelText: string;
  placeholderText: string;
  showPlusMeButton?: boolean;
  size?: "small" | "medium";
  margin?: "none" | "dense" | "normal";
  isRequired?: boolean;
  allowNonXOMEmail?: boolean;
  style?: React.CSSProperties;
}

const VALIDATED_TEXT = "Valid";
const INVALID_TEXT = "Invalid";

const createLabelMap = (
  labelText: string,
  isRequired: boolean,
): Record<ValidationStatus, string> => {
  const normalizedLabel =
    isRequired && !labelText.trimStart().startsWith("*") ? `* ${labelText}` : labelText;
  const cleanedNormalizedLabel = normalizedLabel.replaceAll("*", "").trim();

  return {
    valid: `${isRequired ? "* " : ""}${VALIDATED_TEXT} ${cleanedNormalizedLabel}`,
    invalid: `${isRequired ? "* " : ""}${INVALID_TEXT} ${cleanedNormalizedLabel}`,
    error: `${isRequired ? "* " : ""}${INVALID_TEXT} ${cleanedNormalizedLabel}`,
    idle: normalizedLabel,
    loading: normalizedLabel,
  };
};

const validateEmailAddress = async (email: string): Promise<User | null> => {
  try {
    return await UserApi.searchByEmailAddress(email);
  } catch (error) {
    console.error("Email validation error:", error);
    return null;
  }
};

const EmailAddressTextField: React.FC<EmailAddressTextFieldProps> = ({
  fieldWidth,
  validatedUserEmail,
  setValidatedUserEmail,
  setHasErrors,
  isDisabled = false,
  labelText,
  placeholderText,
  showPlusMeButton = false,
  size,
  margin,
  isRequired = true,
  allowNonXOMEmail = true,
  style = {},
  hasErrors: showAsInvalid = undefined,
  fontSize,
}) => {
  const { username: currentUserEmail } = useUser();
  const [inputValue, setInputValue] = useState<string>("");
  const [displaySearchForUserModal, setDisplaySearchForUserModal] = useState<boolean>(false);
  const [status, setStatus] = useState<ValidationStatus>("idle");

  const labelMap = useMemo(() => createLabelMap(labelText, isRequired), [isRequired, labelText]);
  const label = labelMap[status];

  const displayValue = validatedUserEmail || inputValue;

  useEffect(() => {
    const hasErrors =
      status === "idle"
        ? null
        : status === "valid"
          ? false
          : true;
    setHasErrors(hasErrors);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    if (validatedUserEmail) {
      setStatus("valid");
      setInputValue("");
    } else if (!validatedUserEmail && inputValue === "" && status === "valid") {
      setStatus("idle");
    }
  }, [inputValue, status, validatedUserEmail]);

  const handleFieldChange = (value: string): void => {
    setInputValue(value);

    if (validatedUserEmail) {
      setValidatedUserEmail("");
      setStatus("invalid");
    } else {
      setStatus(value === "" ? "idle" : status);
    }
  };

  const handleClearFieldClick = () => {
    setStatus(isRequired ? "invalid" : "idle");
    setInputValue("");
    setValidatedUserEmail("");
  };

  const handlePlusMeButtonClick = () => {
    if (currentUserEmail) {
      setValidatedUserEmail(currentUserEmail);
      setStatus("valid");
      setInputValue("");
    }
  };

  const handleOkClick = (userData: User) => {
    if (userData) {
      setStatus("valid");
      setValidatedUserEmail(userData.mail);
      setInputValue("");
      setDisplaySearchForUserModal(false);
    }
  };

  const handleCancelClick = () => {
    if (!validatedUserEmail) {
      setStatus("invalid");
    }
    setDisplaySearchForUserModal(false);
  };

  const handleSearchButtonClick = () => {
    setDisplaySearchForUserModal(true);
  };

  const performValidation = useCallback(async (): Promise<void> => {
    if (validatedUserEmail && !inputValue) {
      return;
    }

    if (!inputValue) {
      if (isRequired) {
        setStatus("invalid");
      }
      return;
    }

    if (!inputValue.includes("@")) {
      setStatus("invalid");
      return;
    }

    if (!allowNonXOMEmail && !exxonMobilDomains.some(
        (domain) => inputValue.toLowerCase().trimEnd().endsWith(domain))) {
      setStatus("invalid");
      return;
    }

    try {
      setStatus("loading");
      const user = await validateEmailAddress(inputValue);

      if (user) {
        setStatus("valid");
        setValidatedUserEmail(user.mail);
      } else {
        setStatus("invalid");
      }
    } catch (error) {
      setStatus("error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, isRequired, validatedUserEmail]);

  const handleKeyPress = async (event: React.KeyboardEvent<HTMLDivElement>): Promise<void> => {
    if (event.key === "Enter" || event.key === "Tab") {
      if (event.key === "Tab") {
        event.preventDefault();
      }
      await performValidation();
    }
  };

  const handleOnBlur = async () => {
    await performValidation();
  };

  return (
    <>
      <Box
        display="flex"
        gap={2}
        width={`calc(${fieldWidth} + ${showPlusMeButton ? "64px" : "0px"})`}
        alignItems="center"
        style={style}>
        <ValidationTextField
          label={label}
          value={displayValue}
          onChange={(e) => handleFieldChange(e.target.value)}
          placeholder={placeholderText}
          variant="outlined"
          helperText={undefined}
          disabled={isDisabled}
          size={size ?? "small"}
          margin={margin ?? "normal"}
          InputLabelProps={{ shrink: true }}
          status={status}
          sx={{ flexGrow: 1 }}
          onBlur={handleOnBlur}
          onKeyDown={handleKeyPress}
          InputProps={{
            endAdornment: (
              <>
                {displayValue && !isDisabled && (
                  <InputAdornment position="end">
                    <IconButton
                      disabled={isDisabled}
                      edge="end"
                      sx={{ p: "4px" }}
                      type="button"
                      aria-label="search"
                      onClick={handleClearFieldClick}>
                      <ClearIcon sx={{ width: "20px" }} style={{ fill: "rgba(0, 0, 0, 0.54)" }} />
                    </IconButton>
                  </InputAdornment>
                )}
                {!isDisabled && (
                  <InputAdornment position="end">
                    <IconButton
                      sx={{ p: "2px" }}
                      type="button"
                      aria-label="search"
                      onClick={handleSearchButtonClick}
                      disabled={isDisabled}>
                      <SearchIcon style={{ fill: "blue" }} />
                    </IconButton>
                  </InputAdornment>
                )}
              </>
            ),
          }}
        />
        {showPlusMeButton && (
          <GlobalButton
            name="btnMe"
            disabled={isDisabled}
            variant="contained"
            onClick={handlePlusMeButtonClick}
            style={{ marginTop: margin === "none" ? "0px" : "8px" }}>
            +Me
          </GlobalButton>
        )}
      </Box>
      <UserLookupModal
        display={displaySearchForUserModal}
        initialSearchValue={inputValue}
        onOk={handleOkClick}
        onCancel={handleCancelClick}
      />
    </>
  );
};
export default EmailAddressTextField;
